import React, { useEffect, useState } from "react";
import { Zap, ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useApi } from "../../hooks/useApi";
import { UrssafConnectLogo } from "./UrssafBrandingKit";

const DISMISS_KEY = "urssaf_onboarding_dismissed_at";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

/**
 * Bandeau incitatif sur le Dashboard :
 * - S'affiche si SIRET ou date_naissance manquants sur le profil
 * - Ton "gagne du temps, automatise tes déclarations Urssaf"
 * - Dismiss-able 7 jours (localStorage)
 * - Ne s'affiche jamais pour le compte démo
 */
export const UrssafOnboardingBanner = () => {
  const api = useApi();
  const [profile, setProfile] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const at = localStorage.getItem(DISMISS_KEY);
    if (at && Date.now() - parseInt(at, 10) < DISMISS_DURATION_MS) {
      setDismissed(true);
      return;
    }
    (async () => {
      try {
        const r = await api.get("/auth/me");
        setProfile(r.data);
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (dismissed || !profile) return null;
  // Ne s'affiche pas pour la démo
  if ((profile.email || "").includes("demo@")) return null;

  const hasSiret = !!(profile.siret && profile.siret.replace(/\s/g, "").length === 14);
  const hasDob = !!profile.date_naissance;
  if (hasSiret && hasDob) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div
      data-testid="urssaf-onboarding-banner"
      className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-primary/10 to-emerald-50 p-5 mb-6"
    >
      <button
        onClick={handleDismiss}
        aria-label="Fermer"
        data-testid="urssaf-onboarding-dismiss"
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition"
      >
        <X size={16} />
      </button>

      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
          <Zap size={22} className="text-primary" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <h3 className="font-semibold text-base sm:text-lg leading-tight">
            Gagne du temps : automatise tes déclarations Urssaf
          </h3>
          <UrssafConnectLogo size={34} className="mt-2" />
          <p className="text-sm text-muted-foreground mt-1 mb-3">
            En 30 secondes, connecte ton compte Urssaf pour déclarer et payer tes cotisations
            directement depuis Biz360 — <strong>zéro connexion Urssaf, zéro oubli</strong>.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/parametres#urssaf"
              data-testid="urssaf-onboarding-cta"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition"
            >
              Activer la déclaration 1-clic <ArrowRight size={16} />
            </Link>
            <span className="text-xs text-muted-foreground">
              Il te manque {!hasSiret && !hasDob ? "ton SIRET et ta date de naissance" : !hasSiret ? "ton SIRET" : "ta date de naissance"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UrssafOnboardingBanner;
